import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrandMark } from "@/components/BrandMark";

const SESSION_KEY = "mb-splash-shown";

// 5-second Hinglish splash overlay — shown on first visit / PWA launch,
// fades out smoothly to reveal the storefront. Only shows once per session
// so navigating between routes never re-triggers it.
export const SplashScreen = () => {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    // Never show on admin routes or already-installed PWA re-launches inside a
    // single tab session.
    if (window.location.pathname.startsWith("/masti-backdoor")) return false;
    return !sessionStorage.getItem(SESSION_KEY);
  });

  useEffect(() => {
    if (!visible) return;
    sessionStorage.setItem(SESSION_KEY, "1");
    const t = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          data-testid="splash-screen"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-gradient-to-br from-[#FFF8ED] via-[#FEEFD6] to-[#F4E4BA]"
        >
          {/* Decorative soft mesh */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-70"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(6,78,59,0.10) 0, transparent 45%), radial-gradient(circle at 80% 70%, rgba(224,181,60,0.18) 0, transparent 45%)",
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="relative z-10 flex flex-col items-center gap-6 px-8 text-center"
          >
            <BrandMark size={88} />

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="space-y-2"
            >
              <p
                className="font-display text-4xl sm:text-5xl text-[#0B0C10] tracking-tight"
                data-testid="splash-welcome"
              >
                Welcome
              </p>
              <p
                className="text-sm sm:text-base leading-relaxed text-neutral-800 max-w-xs"
                data-testid="splash-message"
              >
                Dhanyawaad aapko Mastibazaar me swagat hai aapka din subh ho
              </p>
            </motion.div>

            {/* Slim progress bar for the 5-second dwell */}
            <div className="w-40 h-[3px] bg-black/10 rounded-full overflow-hidden mt-2">
              <motion.div
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 5, ease: "linear" }}
                className="h-full bg-gradient-to-r from-[#064E3B] via-[#E0B53C] to-[#064E3B]"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
